import { useState, useEffect } from 'react';
import Button from 'components/ui-components/Button';

import { useGET } from 'hooks/useRequest';
import Input from 'components/cef_form/input';
import VATPrice from 'components/products/vat_price';

import QuickOrdersError from './quick_orders_error';
import QuickOrdersRemoveIcon from './quick_orders_remove_icon';

/**
 * TODO: use useReducer to manage state
 * TODO: this will clean up a lot of code in this file

 * @param {Object} props
 * @param {Function} props.addToOrder - method to add item to basket state
 * @param {Function} props.removeOrder - method to remove item from basket state
 * @param {number} props.id - input row ID
 * @param {boolean} props.vatStatus - to show prices including/excluding VAT
 * @param {Function} props.addNewRow - adds an empty input row
 * @param {Function} props.addInvalidError - adds input row error message
 * @param {Function} props.removeInvalidError - removes an input row error message
 * @param {Function} props.removeInput - method to remove an input row
 * @param {Function} props.undoInputRemoval - method to 'undo' an input row removal
 * @param {boolean} props.setBasketButtonDisabled - to disable the add to basket button
 * @param {Object} props.productLine - data from sessionStorage or copy/paste endpoint
 * @param {Function} props.addToSessionStorage - method to add a row to sessionStorage
 * @returns - quick order search input row
 */
const QuickOrdersSearch = ({
  addToOrder,
  removeOrder,
  id,
  vatStatus,
  addNewRow,
  removeRow,
  addInvalidError,
  removeInvalidError,
  removeInput,
  undoInputRemoval,
  setBasketButtonDisabled,
  addFieldWithQtyMissing,
  removeFieldWithQtyMissing,
  productLine,
  addToSessionStorage,
  removeFromSessionStorage,
}) => {
  // if we have productLine data then we can pre-populate our state values
  const {
    stock_code_input: stockCodeInput,
    product_name: pProductName,
    quantity: productQty,
    step_id: productId,
    stock_level: productStock,
    price: productPrice,
    excVATPrice: productExcVATPrice,
    per: productPer,
    unit: productUnit,
  } = productLine;

  const [makeNewRequest, setMakeNewRequest] = useState(!productLine);

  const [qty, setQty] = useState(productQty || 0);
  const [qtyInput, setQtyInput] = useState(productQty || '');

  const [code, setCode] = useState(stockCodeInput || '');
  const [codeInput, setCodeInput] = useState(stockCodeInput || '');

  const [stepId, setStepId] = useState(productId || '');
  const [productName, setProductName] = useState(pProductName || '');
  const [stockDelivery, setStockDelivery] = useState(productStock || 0);

  const [price, setPrice] = useState(productPrice || 0);
  const [excVATPrice, setExcVATPrice] = useState(productExcVATPrice || 0);
  const [per, setPer] = useState(productPer || 0);
  const [unit, setUnit] = useState(productUnit || '');

  const [isQtyFocussed, setIsQtyFocussed] = useState(false);
  const [qtyAdjusted, setQtyAdjusted] = useState(false);
  const [userAdjustedQty, setUserAdjustedQty] = useState(false);

  const [toggleErr, setToggleErr] = useState('');
  const [err, setErr] = useState([]);
  const [undoOverlay, setUndoOverlay] = useState(false);

  const [currentItemInStorage, setCurrentItemInStorage] = useState({});

  // HTTP request to get data for an input row
  const { data: quickOrderLineData, error: requestError } = useGET(
    code && makeNewRequest
      ? `/quick_order/lines_validation?code=${encodeURIComponent(
          code,
        )}&quantity=${qty}`
      : null,
  );

  /**
   * flags an items we don't want to add to basket
   * @param {Array} errorKeys
   */
  const nonAddableItems = keys =>
    keys.includes('not_found') ||
    keys.includes('collection_only') ||
    keys.includes('third_party_shipped');

  /**
   * flags the row as a missing quantity field
   * @param {number} quantity
   * @param {Array} errorKeys
   */
  const checkMissingQuantityField = (quantity, errorKeys) => {
    if (!quantity && !nonAddableItems(errorKeys)) {
      addFieldWithQtyMissing(id);
    }
  };

  const clearOrder = () => {
    setCode('');
    setCodeInput('');
    setQty('');
    setQtyInput('');
    setProductName('');
    setStockDelivery(0);
    setPrice(0);
    setExcVATPrice(0);
    setPer(0);
    setUnit('');
    setToggleErr('');
    setErr([]);
    removeOrder(id);
    removeRow(id);
    setStepId('');
    removeFieldWithQtyMissing(id);
    removeInvalidError(id);
    setUndoOverlay(false);
    undoInputRemoval(id);
    setBasketButtonDisabled(false);
  };

  const removeInvalidOrder = () => {
    setQty('');
    setQtyInput('');
    setProductName('');
    setStockDelivery(0);
    setPrice(0);
    setExcVATPrice(0);
    setPer(0);
    setUnit('');
    removeOrder(id);
    setStepId('');
  };

  const showUndoOverlay = () => {
    const sessionData =
      JSON.parse(sessionStorage.getItem('manualEntryItems')) || [];
    setCurrentItemInStorage(sessionData.find(item => item.id === id));
    setUndoOverlay(true);
    removeInput(id);
    removeFromSessionStorage(id);
    removeInvalidError(id);
    removeFieldWithQtyMissing(id);
    setBasketButtonDisabled(false);
  };

  const hideUndoOverlay = () => {
    if (currentItemInStorage) {
      addToSessionStorage(currentItemInStorage);
    }
    setUndoOverlay(false);
    undoInputRemoval(id);

    let keys;
    if (
      productLine &&
      productLine.errors &&
      Object.keys(productLine.errors).length > 0
    ) {
      keys = Object.keys(productLine.errors);
    } else if (
      quickOrderLineData &&
      quickOrderLineData.errors &&
      Object.keys(quickOrderLineData.errors).length > 0
    ) {
      keys = Object.keys(quickOrderLineData.errors);
    }

    if (keys && checkMissingQuantityField(qty, keys)) {
      addFieldWithQtyMissing(id);
    }

    if (
      err.includes(
        'Invalid Stock/Part Code, please try again or remove the item',
      )
    ) {
      addInvalidError(id);
    }
  };

  const errorHandler = data => {
    const {
      min_order_quantity: minOrderQty,
      max_order_quantity: maxOrderQty,
      stock_level: stockLevel,
      errors,
      product_prices: productPrices,
      step_id: stepID,
    } = data.length ? data[0] : data;

    const { quantity: validQuantity } = productPrices
      ? productPrices[stepID]
      : {};
    const errorKeys = Object.keys(errors);

    // loop over each error
    for (let i = 0; errorKeys.length > i; i++) {
      const errorKey = errorKeys[i];

      if (errorKey === 'greater_than_max_quantity') {
        // user asks for more than max quantity
        setToggleErr('qty');
        setErr(errorList => [...errorList, errors[errorKey]]);
        setQtyInput(+validQuantity);
        setQty(+validQuantity);
        setQtyAdjusted(true);
      }

      // no stock available
      if (errorKey === 'zero_stock_level') {
        setToggleErr('stock');
        setErr(errorList => [...errorList, errors[errorKey]]);
        setStockDelivery('Out of Stock');
      }

      // user asks for more than avaialble stock
      if (errorKey === 'insufficient_stock_level' && maxOrderQty > stockLevel) {
        setErr(errorList => [...errorList, errors[errorKey]]);
      }

      // user asks for incorrect quantity AND input is more than the min order quantity
      // if quantity input is more than the max order value - do nothing
      // greater_than_max_quantity check will cover
      if (
        errorKey === 'not_divisible_by_min_order' &&
        !errorKeys.includes('less_than_min_order') &&
        +qty >= +minOrderQty &&
        // checking for productLine to persist the message
        (!(+qty > +maxOrderQty) || productLine)
      ) {
        setToggleErr('qty');
        setErr(errorList => [...errorList, errors[errorKey]]);

        // rounding up to the nearest divisible quantity
        setQtyInput(+validQuantity);
        setQty(+validQuantity);
        setQtyAdjusted(true);
      }

      // user asks for less than minimum quantity and there's a set quantity
      if (errorKey === 'less_than_min_order' && !isQtyFocussed) {
        setToggleErr('qty');

        // check for qty changed by the user and less than minimum order qty
        if (qty && +qty < +minOrderQty && (!qtyAdjusted || userAdjustedQty)) {
          setUserAdjustedQty(false);
          setQtyAdjusted(true);
          setQtyInput(+validQuantity);
          setQty(+validQuantity);
          setErr(errorList => [...errorList, errors[errorKey]]);
          // check if the qty has been programmatically adjusted
        } else if (qtyAdjusted) {
          setQtyAdjusted(false);
          setUserAdjustedQty(false);
          setErr(errorList => [...errorList, errors[errorKey]]);
          // checking for storage data and no api response
        } else if (!quickOrderLineData && productLine && qty) {
          setErr(errorList => [...errorList, errors[errorKey]]);
        }
      }

      // user asks for a collection only item
      if (errorKey === 'collection_only') {
        setToggleErr('collection');
        setBasketButtonDisabled(false);
        // filter out of stock message for collection only instances
        setErr([errors[errorKey]]);
        setStockDelivery('Collection Only');
      }

      // user asks for a renewable item
      if (errorKey === 'third_party_shipped') {
        setToggleErr('third_party_shipped');
        setBasketButtonDisabled(false);
        setErr([errors[errorKey]]);
      }

      if (errorKey === 'special_order_message') {
        setErr(errorList => [...errorList, errors[errorKey]]);
      }

      if (errorKey === 'not_found') {
        addInvalidError(id);
        setErr(errorList => [
          ...errorList,
          'Invalid Stock/Part Code, please try again or remove the item',
        ]);
        setToggleErr('product');
        removeInvalidOrder();
      }
    }
  };

  const showPrice = () => {
    if (price) {
      return (
        <VATPrice
          price={price}
          excVATPrice={excVATPrice}
          quantity={per}
          unit={unit}
          excVat={vatStatus}
        />
      );
    }
    return '–';
  };

  const handleUserInput = e => {
    if (code !== e) {
      removeInvalidOrder();
      removeInvalidError(id);
      removeFieldWithQtyMissing(id);
      setErr([]);
      setToggleErr('');
      addNewRow({ id });
      setMakeNewRequest(true);
      setCode(e);
      removeFromSessionStorage(id);
    }
  };

  const handleUserQty = userQty => {
    setIsQtyFocussed(false);
    // If the user deletes the quantity field and the code hasn't changed
    // then refill the quantity field on blur

    setUserAdjustedQty(true);
    setQtyAdjusted(false);

    if (
      !userQty &&
      toggleErr !== 'product' &&
      (code.toLowerCase() === quickOrderLineData?.stock_code?.toLowerCase() ||
        code.toLowerCase() === quickOrderLineData?.part_code?.toLowerCase())
    ) {
      setQty(userQty);
      setQtyInput(userQty);
      setToggleErr('qty');
      addFieldWithQtyMissing(id);
      setMakeNewRequest(false);
      setErr(currErrs =>
        currErrs.filter(
          currError =>
            currError.includes('Estimated delivery') ||
            currError.includes('Out of stock'),
        ),
      );
    } else if (userQty !== qty) {
      removeFieldWithQtyMissing(id);
      setMakeNewRequest(true);
      setQty(userQty);
      setQtyInput(userQty);
      setToggleErr('');
      setErr([]);
    }
  };

  const handleQtyKeyDown = e => {
    const numericPattern = /^[0-9]$/;

    const allowedKeys = [
      'Backspace',
      'Delete',
      'ArrowLeft',
      'ArrowRight',
      'Home',
      'End',
      'Tab',
    ];

    if (!(allowedKeys.includes(e.key) || numericPattern.test(e.key))) {
      e.preventDefault();
    }
  };

  /**
   * method to destructure product prices for quick order row
   * @param {string} stepID
   * @param {Object} productPrices
   * @returns - priceIncVAT, priceExcVAT
   */
  const getPriceBreakData = (stepID, productPrices) => {
    const { quantity: validQuantity } = productPrices
      ? productPrices[stepID]
      : {};

    const priceBreakData = productPrices[stepID].breaks.data.length > 0;

    const priceIncVAT =
      priceBreakData &&
      parseInt(productPrices[stepID].breaks.data[0].per_pack, 10) <=
        validQuantity
        ? parseFloat(
            productPrices[stepID].breaks.data[0].per_price_inclusive,
          ).toFixed(2)
        : parseFloat(productPrices[stepID].per_price_inc_vat).toFixed(2);

    const priceExcVAT =
      priceBreakData &&
      parseInt(productPrices[stepID].breaks.data[0].per_pack) <= validQuantity
        ? parseFloat(productPrices[stepID].breaks.data[0].per_price).toFixed(2)
        : parseFloat(productPrices[stepID].per_price).toFixed(2);

    return { priceIncVAT, priceExcVAT };
  };

  // checking to pre-populate a row
  useEffect(() => {
    const { errors: productLineErrors, quantity } = productLine || {};
    if (productLineErrors && Object.keys(productLineErrors).length > 0) {
      const keys = Object.keys(productLineErrors);
      checkMissingQuantityField(quantity, keys);
      errorHandler(productLine);
    }
  }, [productLine]);

  // if product data returned from API
  useEffect(() => {
    if (quickOrderLineData && !requestError) {
      const {
        step_id: stepID,
        product_name: quickOrderProductName,
        stock_level: quickOrderLineStockLevel,
        stock_code: stockCode,
        part_code: partCode,
        thumbnail,
        total_basket_and_requested_quantity: totalBasketAndRequestedQuant,
        max_order_quantity: maxOrderQty,
        min_order_quantity: minOrderQty,
        errors: quickOrderLineErrors,
        product_prices: productPrices,
      } = quickOrderLineData.length
        ? quickOrderLineData[0]
        : quickOrderLineData;

      setMakeNewRequest(false);
      setUserAdjustedQty(false);

      // clear errors
      setToggleErr('');
      removeInvalidError(id);
      setErr([]);

      // if there are errors
      // check missing quantity field
      // run the error handler
      if (
        quickOrderLineErrors &&
        Object.keys(quickOrderLineErrors).length > 0
      ) {
        const keys = Object.keys(quickOrderLineErrors);
        checkMissingQuantityField(qty, keys);

        errorHandler(quickOrderLineData);
      }

      // displaying product row data
      if (productPrices && Object.keys(productPrices).length > 0) {
        const { priceIncVAT, priceExcVAT } = getPriceBreakData(
          stepID,
          productPrices,
        );

        setStepId(stepID);
        setProductName(quickOrderProductName);
        setStockDelivery(
          quickOrderLineStockLevel > 0
            ? quickOrderLineStockLevel
            : 'Out of Stock',
        );
        setExcVATPrice(priceExcVAT);
        setPrice(priceIncVAT);
        setPer(productPrices[stepID].per_pack);
        setUnit(productPrices[stepID].unit);

        addToSessionStorage({
          id,
          stock_code_input: codeInput.trim(),
          product_id: stepID,
          quantity: +qty,
          product_name: quickOrderProductName,
          step_id: stepID,
          stock_code: stockCode,
          part_code: partCode,
          thumbnail,
          stock_level: quickOrderLineStockLevel,
          total_basket_and_requested_quantity: totalBasketAndRequestedQuant,
          price: priceIncVAT,
          excVATPrice: priceExcVAT,
          per: productPrices[stepID].per_pack,
          unit: productPrices[stepID].unit,
          stockDelivery,
          errors: quickOrderLineErrors,
          max_order_quantity: maxOrderQty,
          min_order_quantity: minOrderQty,
          product_prices: productPrices,
        });
      }
    }
    // let user know call failed
    else if (requestError) {
      addInvalidError(id);
      setErr(errorList => [
        ...errorList,
        'Invalid Stock/Part Code, please try again or remove the item',
      ]);
      setToggleErr('product');
      removeInvalidOrder();
    }
  }, [quickOrderLineData, requestError]);

  // checking if quantity input updated before invoking 'addToOrder()'
  // this is to ensure we are passing in the correct quantity values
  useEffect(() => {
    if (qty && quickOrderLineData) {
      const {
        step_id: stepID,
        product_name: quickOrderProductName,
        stock_code: stockCode,
        part_code: partCode,
        thumbnail,
        stock_level: stockLevel,
        total_basket_and_requested_quantity: totalBasketAndRequestedQuant,
        errors,
        max_order_quantity: maxOrderQty,
        product_prices: productPrices,
      } = quickOrderLineData.length
        ? quickOrderLineData[0]
        : quickOrderLineData;

      setBasketButtonDisabled(false);

      if (qty <= stockLevel) {
        setErr(currErrs =>
          currErrs.filter(
            currError => !currError.includes('Insufficient stock'),
          ),
        );
      }

      if (errors && Object.keys(errors).length > 0) {
        const keys = Object.keys(errors);
        // add item to order array if the key doesn't include collection only and is not found
        // all other errors shouldn't stop an item being available to add to basket
        if (!nonAddableItems(keys)) {
          addToOrder({
            id,
            product_id: stepID,
            quantity: qty,
            product_name: quickOrderProductName,
            step_id: stepID,
            stock_code: stockCode,
            part_code: partCode,
            thumbnail,
            stock_level: stockLevel,
            total_basket_and_requested_quantity: totalBasketAndRequestedQuant,
            max_order_quantity: maxOrderQty,
            product_prices: productPrices,
          });
        }
      } else {
        addToOrder({
          id,
          product_id: stepID,
          quantity: qty,
          product_name: quickOrderProductName,
          step_id: stepID,
          stock_code: stockCode,
          part_code: partCode,
          thumbnail,
          stock_level: stockLevel,
          total_basket_and_requested_quantity: totalBasketAndRequestedQuant,
          max_order_quantity: maxOrderQty,
          product_prices: productPrices,
        });
      }
    }
  }, [quickOrderLineData, qty]);

  // if we have programmatically updated the user quantity input box
  // update sessionStorage with correct quantity value;
  useEffect(() => {
    if (qtyAdjusted && quickOrderLineData) {
      const {
        step_id: stepID,
        product_name: quickOrderProductName,
        stock_level: quickOrderLineStockLevel,
        stock_code: stockCode,
        part_code: partCode,
        thumbnail,
        total_basket_and_requested_quantity: totalBasketAndRequestedQuant,
        max_order_quantity: maxOrderQty,
        min_order_quantity: minOrderQty,
        errors: quickOrderLineErrors,
        product_prices: productPrices,
      } = quickOrderLineData.length
        ? quickOrderLineData[0]
        : quickOrderLineData;

      const { priceIncVAT, priceExcVAT } = getPriceBreakData(
        stepID,
        productPrices,
      );

      addToSessionStorage({
        id,
        stock_code_input: codeInput.trim(),
        product_id: stepID,
        quantity: +qtyInput,
        product_name: quickOrderProductName,
        step_id: stepID,
        stock_code: stockCode,
        part_code: partCode,
        thumbnail,
        stock_level: quickOrderLineStockLevel,
        total_basket_and_requested_quantity: totalBasketAndRequestedQuant,
        price: priceIncVAT,
        excVATPrice: priceExcVAT,
        per: productPrices[stepID].per_pack,
        unit: productPrices[stepID].unit,
        stockDelivery,
        errors: quickOrderLineErrors,
        max_order_quantity: maxOrderQty,
        min_order_quantity: minOrderQty,
        product_prices: productPrices,
      });
    }
  }, [qtyAdjusted]);

  return (
    <>
      <div
        className={`quick-orders__separator fl ${undoOverlay ? '' : 'd-none'}`}>
        <div className="undo-removal__overlay fl ai-c jc-sb">
          <div className="fl ai-c">
            <div className="ml-16">Entry removed from Quick Order list</div>
            <Button
              className="ml-16 fw-500 undo-removal__button"
              handleClick={() => hideUndoOverlay()}
              disabled={undoOverlay === false}>
              Undo
            </Button>
          </div>
          <div className="remove">
            <QuickOrdersRemoveIcon
              cn="ml-32 l-mr-24 mr-12 m-ml-0"
              height={15}
              width={15}
              action={() => clearOrder()}
            />
          </div>
        </div>
      </div>
      <div
        className={`quick-orders__separator fl fl-col pb-10 mt-10 ${
          undoOverlay ? 'd-none' : ''
        }`}>
        <div className="quick-orders__wrapper fl ai-c fl-col m-fl-row">
          {/* Quantity boxes */}
          <div className="quick-orders__column quick-orders__column--stock-and-qty quick-orders__quantity-boxes fl fw-w jc-sb ai-c">
            <div className="fl fl-col fl-1">
              <Input
                ariaLabel="Code Input"
                type="text"
                placeholder="Stock or Part Code"
                inputClassName={`quick-orders__stock-code quick-orders__input ${
                  toggleErr === 'product' ? ' quick-orders__active' : ''
                }`}
                value={codeInput}
                handleBlur={() => handleUserInput(codeInput.trim())}
                handleChange={e => {
                  setCodeInput(e.target.value.trimStart());
                  setBasketButtonDisabled(e.target.value.length > 0);
                }}
              />
            </div>

            <div className="fl fl-col quick_orders__quantity-boxes-num">
              <Input
                ariaLabel="Item Quantity"
                type="number"
                onKeyDown={handleQtyKeyDown}
                inputClassName={`quick-orders__input quick-orders__input--qty nm ta-c ${
                  toggleErr === 'qty' ? ' quick-orders__active' : ''
                }`}
                value={qtyInput}
                handleFocus={() => setIsQtyFocussed(true)}
                handleBlur={() => handleUserQty(qtyInput)}
                handleChange={e => {
                  setQtyInput(e.target.value);
                  setBasketButtonDisabled(e.target.value.length > 0);
                }}
                disabled={
                  toggleErr === 'collection' ||
                  toggleErr === 'third_party_shipped' ||
                  err.includes(
                    'Invalid Stock/Part Code, please try again or remove the item',
                  )
                }
                min={1}
              />
            </div>

            {err &&
              err.map(e => (
                <QuickOrdersError error={e} key={e} cn="l-d-none fl" />
              ))}
          </div>

          {/* Product name */}
          <div
            className={`quick-orders__column quick-orders__column--name pt-8 m-p-0 fw-w fl-col no-mr ${
              stepId || err.length > 0 ? 'fl' : 'd-none m-fl'
            }`}>
            <div className="fl m-fl-col jc-sb ai-c m-ai-n ">
              <p
                className={`quick-orders__product-name no-mb nm pr-16 ${
                  stepId ? 'fl' : 'd-none m-fl'
                }`}>
                {!productName ? '–' : productName}
              </p>
              <div
                className={`quick-orders__column quick-orders__column--remove-icon fl m-d-none fw-w jc-fe ${
                  !stepId && 'width-100'
                }`}>
                <QuickOrdersRemoveIcon
                  cn={`quick-orders__product-remove ml-32 l-mr-24 m-ml-0 mr-12 m-d-none d-block ${
                    !(productName || err.length > 0) && 'quick-orders__hide'
                  }`}
                  height={15}
                  width={15}
                  action={() => showUndoOverlay()}
                />
              </div>
            </div>
          </div>

          {/* Stock + Price */}
          <div
            className={`quick-orders__column quick-orders__column--delivery-and-price fw-w jc-sb ${
              stepId || err.length > 0 ? 'fl' : 'd-none m-fl'
            }`}>
            <div className="fl fl-col as-fe m-as-c width-50">
              <p
                className={`quick-orders__stock-levels ai-fe m-ai-c nm ${
                  stepId ? 'fl' : 'd-none m-fl'
                }`}>
                {!stockDelivery ? (
                  '–'
                ) : (
                  <>
                    <span
                      className={`fw-500 ${
                        toggleErr === 'stock' ||
                        stockDelivery === 'Out of Stock' ||
                        stockDelivery === 'Collection Only'
                          ? 'tc-cef-red'
                          : 'quick-orders__stock-levels-amount'
                      }`}>
                      {stockDelivery}
                    </span>
                    <span
                      className={
                        toggleErr === 'stock' ||
                        toggleErr === 'collection' ||
                        toggleErr === 'third_party_shipped'
                          ? 'd-none'
                          : 'tc-grey-70 fs-14 d-block m-d-none ml-3'
                      }>
                      in stock
                    </span>
                  </>
                )}
              </p>
            </div>

            <div className="fl fl-col quick-orders__column-stock width-50">
              <div
                className={`quick-orders__product-price jc-fe m-jc-fs ${
                  stepId ? 'fl' : 'd-none m-fl'
                }`}>
                {showPrice()}
              </div>
            </div>
          </div>

          {/* Remove icon desktop */}
          <div className="quick-orders__column quick-orders__column--remove-icon m-fl m-jc-fe fw-w d-none">
            <QuickOrdersRemoveIcon
              cn={`quick-orders__product-remove ml-32 l-mr-24 mr-12 m-ml-0 m-d-block d-none ${
                !(productName || err.length > 0) && 'quick-orders__hide'
              }`}
              disabled={!(productName || err.length > 0)}
              height={15}
              width={15}
              action={() => showUndoOverlay()}
            />
          </div>
        </div>
        {err &&
          err.map(e => <QuickOrdersError error={e} key={e} cn="l-fl d-none" />)}
      </div>
    </>
  );
};

export default QuickOrdersSearch;
